import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Modal, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Input from 'components/Common/Input';
import { useFieldArray, useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'components/Common/Select';
import {
  useCreateIntegration,
  useCreateOauthIntegration,
  useGetAvailableIntegrations,
  useGetCalenders,
  useGetIntegration,
  useGetNewIntegrationsFields,
  useUpdateIntegration
} from 'apis/business/setting/integrations/hook';
import Spinner from 'components/Common/Spinner';
import { IoChevronDownOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';

export default function AddIntegrationForm({ isOpen, handleClose }: { isOpen?: boolean; handleClose?: any }) {
  const { workspaceId, integrationId } = useParams();
  const navigate = useNavigate();

  const { mutateAsync: createIntegration, isPending: creatingIntegration } = useCreateIntegration();
  const { mutateAsync: updateIntegration, isPending: updatingIntegration } = useUpdateIntegration();
  const { mutateAsync: handleCreateOauthIntegration } = useCreateOauthIntegration();
  const { data: IntegrationData, isLoading: isIntegrationDataLoading } = useGetIntegration(Number(integrationId));
  const { data: availableIntegrations, isLoading: isAvailableIntegrationLoading } = useGetAvailableIntegrations();

  const { handleSubmit, control, watch, reset } = useForm({ values: { ...IntegrationData } });
  const selectedIntegrationCode = watch('code');
  const selectedIntegrationName = watch('name');
  const selectedIntegrationData = availableIntegrations?.data?.find(
    (item: any) => item.code === selectedIntegrationCode
  );

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: 'configuration.headers'
  });

  const { data: formFields } = useGetNewIntegrationsFields(selectedIntegrationCode);
  const { data: calenders, isLoading: isCalenderLoading } = useGetCalenders(
    Number(IntegrationData?.id),
    selectedIntegrationData?.category
  );

  const handleAuthentication = () => {
    if (!IntegrationData?.active) {
      handleCreateOauthIntegration({
        code: selectedIntegrationCode,
        name: selectedIntegrationName
      });
      navigate(`/setting/workspace/${workspaceId}/integration`);
    }
  };

  const onSubmit = async (data: any) => {
    if (integrationId) {
      data.integration_name = formFields?.integration_name;
      await updateIntegration(data);
      handleClose ? handleClose() : navigate(`/setting/workspace/${workspaceId}/integration`);
      reset();
    } else {
      data.integration_name = formFields?.integration_name;
      await createIntegration(data);
      handleClose ? handleClose() : navigate(`/setting/workspace/${workspaceId}/integration`);
      reset();
    }
  };

  if (isIntegrationDataLoading) {
    return <Spinner />;
  }

  return (
    <Modal
      open={isOpen === undefined ? true : isOpen}
      onClose={() => (handleClose ? handleClose() : navigate(`/setting/workspace/${workspaceId}/integration`))}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Typography variant='h6'>Add New Integration</Typography>
        <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
          <Grid container spacing='26px'>
            <Grid size={12}>
              <Input
                name='name'
                label='Name'
                placeholder='Enter a friendly name for this integration'
                control={control}
                fullWidth
                required
              />
            </Grid>
            <Grid size={12}>
              <Select
                name='code'
                disabled={isAvailableIntegrationLoading}
                label='Select Integration'
                control={control}
                options={
                  availableIntegrations?.data?.map((integration) => ({
                    value: integration.code,
                    label: integration.name
                  })) || []
                }
              />
              {selectedIntegrationData?.connection_type === 'oauth' ? (
                <Box
                  borderTop='1px solid #F3F3F3'
                  paddingY={2}
                  display='flex'
                  flexDirection='column'
                  gap={1.5}
                  marginTop={2}
                >
                  <Typography fontWeight={600}>OAuth2 Authentication</Typography>
                  <Typography color='secondary' fontSize={16}>
                    {selectedIntegrationData.name} requires OAuth2 authentication to access your data.
                  </Typography>
                  <Button
                    sx={{ opacity: IntegrationData?.active ? '50%' : '100%' }}
                    onClick={() => handleAuthentication()}
                  >
                    {IntegrationData?.active
                      ? `Authenticated with ${selectedIntegrationData.name}`
                      : `Authenticate with ${selectedIntegrationData.name}`}
                  </Button>
                </Box>
              ) : (
                <>
                  {formFields?.configuration?.map((configField: { key: string; required: boolean }, idx: number) =>
                    configField.key === 'headers' ? (
                      <Accordion
                        sx={{
                          boxShadow: 'none',
                          border: '1px solid #F3F3F3',
                          marginTop: 2,
                          '::before': { background: 'transparent' }
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                          aria-controls='panel1-content'
                          id='panel1-header'
                        >
                          <Box display='flex' flexDirection='column'>
                            <Typography variant='h6' fontSize={16} fontWeight={600}>
                              Headers
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {headerFields.map((field: any, index: number) => (
                            <Box
                              key={field.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                mb: 3
                              }}
                            >
                              <Input
                                name={`configuration.headers[${index}].field_name`}
                                label='Field name'
                                control={control}
                                sx={{ flex: 2 }}
                                fullWidth
                              />
                              <Input
                                name={`configuration.headers[${index}].value`}
                                label='Value'
                                control={control}
                                sx={{ flex: 2 }}
                                fullWidth
                              />
                              <IconButton color='error' onClick={() => headerRemove(index)}>
                                <MdDelete />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            type='button'
                            color='secondary'
                            size='small'
                            variant='outlined'
                            startIcon={<FaPlus />}
                            onClick={() => headerAppend({ field_name: '', description: '' })}
                            sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                          >
                            Add Field
                          </Button>
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <Grid size={12} marginY={2.5}>
                        <Input
                          key={idx}
                          name={`configuration.${configField?.key}`}
                          control={control}
                          label={configField?.key.replace(/_/g, ' ')}
                          fullWidth
                          required={configField.required}
                        />
                      </Grid>
                    )
                  )}
                </>
              )}
              {IntegrationData?.active && selectedIntegrationData?.category === 'calendar_management' && (
                <>
                  <Box mt={2}>
                    <Select
                      name='configuration.calendar_id'
                      label='Select Calendar'
                      isLoading={isCalenderLoading}
                      control={control}
                      options={calenders ?? []}
                    />
                  </Box>
                  <Box mt={2}>
                    <Select
                      name='configuration.booking_slot'
                      label='Booking Slot'
                      control={control}
                      options={[
                        { value: '30', label: '30 Minutes' },
                        { value: '45', label: '45 Minutes' },
                        { value: '60', label: '60 Minutes' },
                        { value: '120', label: '120 Minutes' }
                      ]}
                    />
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: '16px',
              marginTop: '24px'
            }}
          >
            <Button
              color='secondary'
              variant='outlined'
              sx={{ border: '1px solid #F3F3F3' }}
              onClick={() => (handleClose ? handleClose() : navigate(-1))}
            >
              Cancel
            </Button>
            {(selectedIntegrationData?.connection_type !== 'oauth' || IntegrationData?.active) && (
              <Button type='submit' variant='contained' isPending={updatingIntegration || creatingIntegration}>
                Save
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  maxHeight: '600px',
  overflow: 'auto',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  width: {
    xs: '90%',
    sm: 200,
    md: 400,
    lg: 600
  }
};
